import { NumberInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { ReturnReconciliationListItem } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { client } from "@/hono";

/** Props Interface */
interface BookReturnModalProps extends ModalProps {
	returnOrderId: string;
	row: ReturnReconciliationListItem;
}

/** The one field a booking needs, plus the shelf it defaults to. */
interface BookReturnFields {
	quantity: number;
	articleLocationId: string;
}

/**
 * Book one article back onto a shelf.
 *
 * The quantity defaults to what is still outstanding, because putting back everything still on
 * site is the common case — and it is editable because a truck rarely brings all of it at once.
 * Nothing bounds it: more can come back than this project's orders recorded.
 *
 * The shelf defaults to the one this article was picked from for this project, which the ledger
 * knows now that a pick records its round.
 */
export function BookReturnModal({ isOpen, onClose, returnOrderId, row }: BookReturnModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm<BookReturnFields>({
		defaultValues: {
			// Negative outstanding means more is already back than went out; offering a negative
			// default would be nonsense, so it falls back to one.
			quantity: row.outstandingQuantity > 0 ? row.outstandingQuantity : 1,
			articleLocationId: row.lastPickedArticleLocationId ?? "",
		},
	});

	/** Functions */
	async function handleOnSubmit(json: BookReturnFields) {
		const response = await client.stock["return-orders"][":returnOrderId"].bookings.$post({
			param: { returnOrderId },
			json: { articleId: row.articleId, quantity: json.quantity },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["return-order", returnOrderId]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={`${t("stock.return_book")} — ${row.articleName}`}
				isLoading={methods.formState.isLoading}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<NumberInput
						label={t("stock.return_book_quantity")}
						control={methods.control}
						name="quantity"
						isRequired
					/>
					{/*
					 * No shelf picker yet: the ledger's own record of where this article was picked from
					 * for this project is the destination, and a run whose picks predate that anchor has
					 * none to offer. Choosing a different shelf is the article-location transaction
					 * endpoint's job, which this modal is a narrow front for.
					 */}
					{!row.lastPickedArticleLocationId && (
						<p className="text-amber-600 text-xs">{t("stock.return_book_location")}: --</p>
					)}
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
